import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import {
  RoomService,
  Download,
  TrendingUp,
  AttachMoney,
} from '@mui/icons-material';
import { useSuperadminServiceUsageReport } from '../../hooks/useSuperAdmin';
import { colors } from '../../theme/theme';
import { DateFilter, DateFilterValue } from '../../components/common';

export const ServiceUsageReportPage = () => {
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('all');
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();

  const { report, isLoading, error } = useSuperadminServiceUsageReport({
    date_filter: dateFilter,
    start_date: startDate,
    end_date: endDate,
  });

  const handleDateFilterChange = (filter: DateFilterValue, start?: string, end?: string) => {
    setDateFilter(filter);
    setStartDate(start);
    setEndDate(end);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error">Failed to load service usage report</Alert>
      </AdminLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const serviceColumns: GridColDef[] = [
    {
      field: 'service_name',
      headerName: 'Service Name',
      flex: 1.5,
      renderCell: (params) => (
        <Typography fontWeight={500}>{params.value}</Typography>
      ),
    },
    {
      field: 'service_code',
      headerName: 'Code',
      flex: 0.8,
    },
    {
      field: 'county_name',
      headerName: 'County',
      flex: 1,
      renderCell: (params) => (
        <Typography>{params.value}</Typography>
      ),
    },
    {
      field: 'total_transactions',
      headerName: 'Total Usage',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: colors.primary.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'successful_transactions',
      headerName: 'Successful',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            color: colors.success.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'failed_transactions',
      headerName: 'Failed',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: 'rgba(244, 67, 54, 0.1)',
            color: colors.error.main,
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'total_revenue',
      headerName: 'Revenue',
      flex: 1.2,
      renderCell: (params) => (
        <Typography fontWeight={600} color={colors.success.main}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'last_used',
      headerName: 'Last Used',
      flex: 1.2,
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            System Service Usage Reports
          </Typography>
          <Box display="flex" gap={2}>
            <DateFilter
              value={dateFilter}
              onChange={handleDateFilterChange}
              startDate={startDate}
              endDate={endDate}
            />
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                },
              }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Services
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.totalServices || 0}
                    </Typography>
                  </Box>
                  <RoomService sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Active Services
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {report?.activeServices || 0}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Avg Usage
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {Math.round(report?.averageUsage || 0)}
                    </Typography>
                  </Box>
                  <RoomService sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="white" mt={1}>
                      {formatCurrency(report?.totalRevenue || 0).replace('KES', '').trim()}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Service Usage Breakdown
            </Typography>
            {report?.serviceStats && report.serviceStats.length > 0 ? (
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={report.serviceStats}
                  columns={serviceColumns}
                  getRowId={(row) => row.service_id}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No service usage data available for the selected period
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
